import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SkeletonLoaderComponent } from '@shared/skeleton-loader/skeleton-loader.component';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { settingsService } from '@features/settings/setting-services/setting-service';
import { SendDataService } from '@core/services/send-data.service';
import { ToastService } from '@core/services/toast.service';
import { ModalService } from '@shared/services/modal.service';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [CommonModule, SkeletonLoaderComponent, ReactiveFormsModule],
  template: `
    <ng-template #passwordFormTpl>
      <div class="password-change-view p-0">
        <div class="flex items-center pb-5">
          <button
            type="button"
            (click)="hidePasswordForm()"
            class="mr-2 text-gray-700 focus:outline-none"
          >
            <span class="inline-block align-middle"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M14.5 17L9.5 12L14.5 7" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
</span>
          </button>
          <h2 class="text-[16px] font-[500] text-gray-900 flex-1 text-left">Change Password</h2>
          <button class="p-1  rounded-full transition-colors" (click)="onClose()">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 17L17 12L12 7"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 7L12 12L7 17"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <form [formGroup]="passwordForm" (ngSubmit)="submitPasswordForm()" class="password-form-shell">
          <div class="password-form-content p-2">
          <div class="mb-6">
            <label class="block text-gray-700 font-semibold mb-2"
              >Current Password
              <span
                *ngIf="
                  passwordForm.get('currentPassword')?.valid &&
                  passwordForm.get('currentPassword')?.touched
                "
                class="ml-2 text-green-600 align-middle"
                ></span
              >
            </label>
            <div
              [ngClass]="{
                'border-green-500': passwordForm.get('currentPassword')?.valid && passwordForm.get('currentPassword')?.touched
              }"
              class="rounded-[12px] flex items-center px-4 py-1 bg-white focus-within:ring-2 focus-within:ring-[#1e293b] transition-all border border-gray-300"
            >
              <input
                [type]="passwordVisible ? 'text' : 'password'"
                formControlName="currentPassword"
                class="flex-1 outline-none bg-transparent text-lg placeholder-gray-400 border-none shadow-none"
                style="font-family: inherit;"
              />
              <button
                type="button"
                (click)="togglePassword('current')"
                class="password-visibility-toggle ml-2 focus:outline-none transition-colors"
                [ngClass]="passwordForm.get('currentPassword')?.value ? 'text-gray-700' : 'text-gray-400'"
              >
                <!-- <svg *ngIf="!passwordVisible" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-.274.832-.64 1.624-1.09 2.354M15.54 15.54A8.963 8.963 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.014 9.014 0 012.042-3.354M9.88 9.88A3 3 0 0115 12m0 0a3 3 0 01-5.12-2.12" /></svg> -->
                <svg
                  *ngIf="!passwordVisible"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.66675 9.99999C1.66675 11.3667 2.02091 11.8258 2.72925 12.7467C4.14341 14.5833 6.51508 16.6667 10.0001 16.6667C13.4851 16.6667 15.8567 14.5833 17.2709 12.7467C17.9792 11.8267 18.3334 11.3658 18.3334 9.99999C18.3334 8.63333 17.9792 8.17416 17.2709 7.25333C15.8567 5.41666 13.4851 3.33333 10.0001 3.33333C6.51508 3.33333 4.14341 5.41666 2.72925 7.25333C2.02091 8.17499 1.66675 8.63416 1.66675 9.99999ZM10.0001 6.87499C9.17128 6.87499 8.37642 7.20423 7.79037 7.79029C7.20432 8.37634 6.87508 9.17119 6.87508 9.99999C6.87508 10.8288 7.20432 11.6237 7.79037 12.2097C8.37642 12.7958 9.17128 13.125 10.0001 13.125C10.8289 13.125 11.6237 12.7958 12.2098 12.2097C12.7958 11.6237 13.1251 10.8288 13.1251 9.99999C13.1251 9.17119 12.7958 8.37634 12.2098 7.79029C11.6237 7.20423 10.8289 6.87499 10.0001 6.87499Z"
                    fill="currentColor"
                  />
                  <path
                    d="M16.4071 1.68587L18.0333 3.31208L4.21915 17.1262L2.59294 15.5L16.4071 1.68587Z"
                    fill="currentColor"
                    stroke="currentColor"
                  />
                </svg>

                <!-- <svg *ngIf="passwordVisible" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.014 9.014 0 012.042-3.354M15.54 15.54A8.963 8.963 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.014 9.014 0 012.042-3.354M9.88 9.88A3 3 0 0115 12m0 0a3 3 0 01-5.12-2.12" /></svg> -->
                <svg
                  *ngIf="passwordVisible"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.125 10C8.125 9.50272 8.32254 9.02581 8.67417 8.67417C9.02581 8.32254 9.50272 8.125 10 8.125C10.4973 8.125 10.9742 8.32254 11.3258 8.67417C11.6775 9.02581 11.875 9.50272 11.875 10C11.875 10.4973 11.6775 10.9742 11.3258 11.3258C10.9742 11.6775 10.4973 11.875 10 11.875C9.50272 11.875 9.02581 11.6775 8.67417 11.3258C8.32254 10.9742 8.125 10.4973 8.125 10Z"
                    fill="currentColor"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.66675 9.99999C1.66675 11.3667 2.02091 11.8258 2.72925 12.7467C4.14341 14.5833 6.51508 16.6667 10.0001 16.6667C13.4851 16.6667 15.8567 14.5833 17.2709 12.7467C17.9792 11.8267 18.3334 11.3658 18.3334 9.99999C18.3334 8.63333 17.9792 8.17416 17.2709 7.25333C15.8567 5.41666 13.4851 3.33333 10.0001 3.33333C6.51508 3.33333 4.14341 5.41666 2.72925 7.25333C2.02091 8.17499 1.66675 8.63416 1.66675 9.99999ZM10.0001 6.87499C9.17128 6.87499 8.37642 7.20423 7.79037 7.79029C7.20432 8.37634 6.87508 9.17119 6.87508 9.99999C6.87508 10.8288 7.20432 11.6237 7.79037 12.2097C8.37642 12.7958 9.17128 13.125 10.0001 13.125C10.8289 13.125 11.6237 12.7958 12.2098 12.2097C12.7958 11.6237 13.1251 10.8288 13.1251 9.99999C13.1251 9.17119 12.7958 8.37634 12.2098 7.79029C11.6237 7.20423 10.8289 6.87499 10.0001 6.87499Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="mb-6">
            <label class="block text-gray-700 font-semibold mb-2">New Password</label>
            <div
              [ngClass]="{
                'border-red-400': !newPasswordValid && (passwordForm.get('newPassword')?.touched || passwordForm.get('newPassword')?.dirty || passwordTouched)
              }"
              class="rounded-[12px] flex items-center px-4 py-1 bg-white focus-within:ring-2 focus-within:ring-[#1e293b] transition-all border"
            >
              <input
                [type]="newPasswordVisible ? 'text' : 'password'"
                formControlName="newPassword"
                class="flex-1 outline-none bg-transparent text-lg placeholder-gray-400 border-none shadow-none"
                style="font-family: inherit;"
                autocomplete="new-password"
              />
              <button
                type="button"
                (click)="togglePassword('new')"
                class="password-visibility-toggle ml-2 focus:outline-none transition-colors"
                [ngClass]="passwordForm.get('newPassword')?.value ? 'text-gray-700' : 'text-gray-400'"
              >
        
       

                <svg
                  *ngIf="!newPasswordVisible"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.66675 9.99999C1.66675 11.3667 2.02091 11.8258 2.72925 12.7467C4.14341 14.5833 6.51508 16.6667 10.0001 16.6667C13.4851 16.6667 15.8567 14.5833 17.2709 12.7467C17.9792 11.8267 18.3334 11.3658 18.3334 9.99999C18.3334 8.63333 17.9792 8.17416 17.2709 7.25333C15.8567 5.41666 13.4851 3.33333 10.0001 3.33333C6.51508 3.33333 4.14341 5.41666 2.72925 7.25333C2.02091 8.17499 1.66675 8.63416 1.66675 9.99999ZM10.0001 6.87499C9.17128 6.87499 8.37642 7.20423 7.79037 7.79029C7.20432 8.37634 6.87508 9.17119 6.87508 9.99999C6.87508 10.8288 7.20432 11.6237 7.79037 12.2097C8.37642 12.7958 9.17128 13.125 10.0001 13.125C10.8289 13.125 11.6237 12.7958 12.2098 12.2097C12.7958 11.6237 13.1251 10.8288 13.1251 9.99999C13.1251 9.17119 12.7958 8.37634 12.2098 7.79029C11.6237 7.20423 10.8289 6.87499 10.0001 6.87499Z"
                    fill="currentColor"
                  />
                  <path
                    d="M16.4071 1.68587L18.0333 3.31208L4.21915 17.1262L2.59294 15.5L16.4071 1.68587Z"
                    fill="currentColor"
                    stroke="currentColor"
                  />
                </svg>

                <!-- <svg *ngIf="passwordVisible" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.014 9.014 0 012.042-3.354M15.54 15.54A8.963 8.963 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.014 9.014 0 012.042-3.354M9.88 9.88A3 3 0 0115 12m0 0a3 3 0 01-5.12-2.12" /></svg> -->
                <svg
                  *ngIf="newPasswordVisible"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.125 10C8.125 9.50272 8.32254 9.02581 8.67417 8.67417C9.02581 8.32254 9.50272 8.125 10 8.125C10.4973 8.125 10.9742 8.32254 11.3258 8.67417C11.6775 9.02581 11.875 9.50272 11.875 10C11.875 10.4973 11.6775 10.9742 11.3258 11.3258C10.9742 11.6775 10.4973 11.875 10 11.875C9.50272 11.875 9.02581 11.6775 8.67417 11.3258C8.32254 10.9742 8.125 10.4973 8.125 10Z"
                    fill="currentColor"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.66675 9.99999C1.66675 11.3667 2.02091 11.8258 2.72925 12.7467C4.14341 14.5833 6.51508 16.6667 10.0001 16.6667C13.4851 16.6667 15.8567 14.5833 17.2709 12.7467C17.9792 11.8267 18.3334 11.3658 18.3334 9.99999C18.3334 8.63333 17.9792 8.17416 17.2709 7.25333C15.8567 5.41666 13.4851 3.33333 10.0001 3.33333C6.51508 3.33333 4.14341 5.41666 2.72925 7.25333C2.02091 8.17499 1.66675 8.63416 1.66675 9.99999ZM10.0001 6.87499C9.17128 6.87499 8.37642 7.20423 7.79037 7.79029C7.20432 8.37634 6.87508 9.17119 6.87508 9.99999C6.87508 10.8288 7.20432 11.6237 7.79037 12.2097C8.37642 12.7958 9.17128 13.125 10.0001 13.125C10.8289 13.125 11.6237 12.7958 12.2098 12.2097C12.7958 11.6237 13.1251 10.8288 13.1251 9.99999C13.1251 9.17119 12.7958 8.37634 12.2098 7.79029C11.6237 7.20423 10.8289 6.87499 10.0001 6.87499Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <div
              *ngIf="
                passwordForm.get('newPassword')?.value &&
                !newPasswordValid &&
                (passwordForm.get('newPassword')?.touched || passwordForm.get('newPassword')?.dirty || passwordTouched)
              "
              class="text-red-500 mt-2 text-sm"
            >
              Please add all the necessary characters!
            </div>
            <ul
              class="mt-2 text-sm"
              *ngIf="
                passwordForm.get('newPassword')?.touched || passwordForm.get('newPassword')?.dirty || passwordTouched
              "
            >
              <li
                [ngClass]="{
                  'text-[#5cb85c]': passwordRequirements.minLength,
                  'text-red-500': !passwordRequirements.minLength,
                }"
              >
                ● Minimum 8 characters
              </li>
              <li
                [ngClass]="{
                  'text-[#5cb85c]': passwordRequirements.lowercase,
                  'text-red-500': !passwordRequirements.lowercase,
                }"
              >
                ● One lowercase character
              </li>
              <li
                [ngClass]="{
                  'text-[#5cb85c]': passwordRequirements.uppercase,
                  'text-red-500': !passwordRequirements.uppercase,
                }"
              >
                ● One uppercase character
              </li>
              <li
                [ngClass]="{
                  'text-[#5cb85c]': passwordRequirements.special,
                  'text-red-500': !passwordRequirements.special,
                }"
              >
                ● One special character
              </li>
            </ul>
          </div>
          <div class="mb-6">
            <label class="block text-gray-700 font-semibold mb-2">Confirm New Password</label>
            <div
              class="border border-gray-300 rounded-[12px] flex items-center px-4 py-1 bg-white focus-within:ring-2 focus-within:ring-[#1e293b] transition-all"
            >
              <input
                [type]="confirmPasswordVisible ? 'text' : 'password'"
                formControlName="confirmPassword"
                class="flex-1 outline-none bg-transparent text-lg placeholder-gray-400 border-none shadow-none"
                style="font-family: inherit;"
              />
              <button
                type="button"
                (click)="togglePassword('confirm')"
                class="password-visibility-toggle ml-2 focus:outline-none transition-colors"
                [ngClass]="passwordForm.get('confirmPassword')?.value ? 'text-gray-700' : 'text-gray-400'"
              >
                <svg
                  *ngIf="!confirmPasswordVisible"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.66675 9.99999C1.66675 11.3667 2.02091 11.8258 2.72925 12.7467C4.14341 14.5833 6.51508 16.6667 10.0001 16.6667C13.4851 16.6667 15.8567 14.5833 17.2709 12.7467C17.9792 11.8267 18.3334 11.3658 18.3334 9.99999C18.3334 8.63333 17.9792 8.17416 17.2709 7.25333C15.8567 5.41666 13.4851 3.33333 10.0001 3.33333C6.51508 3.33333 4.14341 5.41666 2.72925 7.25333C2.02091 8.17499 1.66675 8.63416 1.66675 9.99999ZM10.0001 6.87499C9.17128 6.87499 8.37642 7.20423 7.79037 7.79029C7.20432 8.37634 6.87508 9.17119 6.87508 9.99999C6.87508 10.8288 7.20432 11.6237 7.79037 12.2097C8.37642 12.7958 9.17128 13.125 10.0001 13.125C10.8289 13.125 11.6237 12.7958 12.2098 12.2097C12.7958 11.6237 13.1251 10.8288 13.1251 9.99999C13.1251 9.17119 12.7958 8.37634 12.2098 7.79029C11.6237 7.20423 10.8289 6.87499 10.0001 6.87499Z"
                    fill="currentColor"
                  />
                  <path
                    d="M16.4071 1.68587L18.0333 3.31208L4.21915 17.1262L2.59294 15.5L16.4071 1.68587Z"
                    fill="currentColor"
                    stroke="currentColor"
                  />
                </svg>

                <!-- <svg *ngIf="passwordVisible" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.014 9.014 0 012.042-3.354M15.54 15.54A8.963 8.963 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.014 9.014 0 012.042-3.354M9.88 9.88A3 3 0 0115 12m0 0a3 3 0 01-5.12-2.12" /></svg> -->
                <svg
                  *ngIf="confirmPasswordVisible"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.125 10C8.125 9.50272 8.32254 9.02581 8.67417 8.67417C9.02581 8.32254 9.50272 8.125 10 8.125C10.4973 8.125 10.9742 8.32254 11.3258 8.67417C11.6775 9.02581 11.875 9.50272 11.875 10C11.875 10.4973 11.6775 10.9742 11.3258 11.3258C10.9742 11.6775 10.4973 11.875 10 11.875C9.50272 11.875 9.02581 11.6775 8.67417 11.3258C8.32254 10.9742 8.125 10.4973 8.125 10Z"
                    fill="currentColor"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M1.66675 9.99999C1.66675 11.3667 2.02091 11.8258 2.72925 12.7467C4.14341 14.5833 6.51508 16.6667 10.0001 16.6667C13.4851 16.6667 15.8567 14.5833 17.2709 12.7467C17.9792 11.8267 18.3334 11.3658 18.3334 9.99999C18.3334 8.63333 17.9792 8.17416 17.2709 7.25333C15.8567 5.41666 13.4851 3.33333 10.0001 3.33333C6.51508 3.33333 4.14341 5.41666 2.72925 7.25333C2.02091 8.17499 1.66675 8.63416 1.66675 9.99999ZM10.0001 6.87499C9.17128 6.87499 8.37642 7.20423 7.79037 7.79029C7.20432 8.37634 6.87508 9.17119 6.87508 9.99999C6.87508 10.8288 7.20432 11.6237 7.79037 12.2097C8.37642 12.7958 9.17128 13.125 10.0001 13.125C10.8289 13.125 11.6237 12.7958 12.2098 12.2097C12.7958 11.6237 13.1251 10.8288 13.1251 9.99999C13.1251 9.17119 12.7958 8.37634 12.2098 7.79029C11.6237 7.20423 10.8289 6.87499 10.0001 6.87499Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
          </div>
          <div class="password-submit-footer flex justify-end">
            <button
              type="submit"
              class="password-submit-btn button-color text-white rounded shadow"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </ng-template>
    <div *ngIf="!user" class="profile-skeleton-shell">
      <div class="profile-skeleton-card">
        <div class="profile-skeleton-hero">
          <div class="profile-skeleton-avatar"></div>
          <div class="profile-skeleton-hero-lines">
            <div class="profile-skeleton-line short"></div>
            <div class="profile-skeleton-line"></div>
            <div class="profile-skeleton-line long"></div>
          </div>
          <div class="profile-skeleton-actions">
            <div class="profile-skeleton-action-pill"></div>
            <div class="profile-skeleton-action-icon"></div>
          </div>
        </div>
        <div class="profile-skeleton-section">
          <div class="profile-skeleton-label"></div>
          <div class="profile-skeleton-row">
            <div class="profile-skeleton-line medium"></div>
            <div class="profile-skeleton-line medium"></div>
          </div>
        </div>
        <div class="profile-skeleton-section">
          <div class="profile-skeleton-label"></div>
          <div class="profile-skeleton-row">
            <div class="profile-skeleton-line"></div>
            <div class="profile-skeleton-line"></div>
          </div>
        </div>
        <div class="profile-skeleton-section last">
          <div class="profile-skeleton-label"></div>
          <div class="profile-skeleton-row">
            <div class="profile-skeleton-line long"></div>
            <div class="profile-skeleton-line short"></div>
          </div>
        </div>
              <div class="profile-skeleton-section last">
          <div class="profile-skeleton-label"></div>
          <div class="profile-skeleton-row">
            <div class="profile-skeleton-line long"></div>
            <div class="profile-skeleton-line short"></div>
          </div>
        </div>
              <div class="profile-skeleton-section last">
          <div class="profile-skeleton-label"></div>
          <div class="profile-skeleton-row">
            <div class="profile-skeleton-line long"></div>
            <div class="profile-skeleton-line short"></div>
          </div>
        </div>
              <div class="profile-skeleton-section last">
          <div class="profile-skeleton-label"></div>
          <div class="profile-skeleton-row">
            <div class="profile-skeleton-line long"></div>
            <div class="profile-skeleton-line short"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full  bg-white p-0 md:p-6 rounded-xl  text-sm" *ngIf="user">
      <!-- Header -->
      <div class="panel-inner-header flex items-center justify-between border-gray-200" *ngIf="!showChangePassword">
        <h2 class="text-[16px] font-[500] text-gray-900">User Profile</h2>
        <button class="p-1  rounded-full transition-colors" (click)="onClose()">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 17L17 12L12 7"
              stroke="black"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7 7L12 12L7 17"
              stroke="black"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <ng-container *ngIf="!showChangePassword; else passwordFormTpl">
        <!-- Profile Info -->

        <div class="pt-6 md:pt-[35px] md:px-[36px]">
          <div class="flex items-start gap-3 mb-3">
            <div class="relative group w-[77px] h-[77px] md:w-[120px] md:h-[120px]">
              <img
                class="w-[77px] h-[77px] md:w-[120px] md:h-[120px] rounded-full object-cover transition duration-300 group-hover:blur-sm"
                [src]="user.profile_image || '/assets/images/profile.png'"
                alt="User"
              />
              <input
                type="file"
                accept="image/*"
                #fileInput
                (change)="onProfileImageChange($event)"
                class="hidden"
              />
              <div
                class="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                (click)="fileInput.click()"
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 20 18"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M7.778 18H12.222C15.343 18 16.904 18 18.025 17.265C18.5088 16.9481 18.9254 16.539 19.251 16.061C20 14.961 20 13.428 20 10.364C20 7.3 20 5.767 19.251 4.667C18.9254 4.18899 18.5088 3.77986 18.025 3.463C17.305 2.99 16.403 2.821 15.022 2.761C14.363 2.761 13.796 2.271 13.667 1.636C13.5684 1.17087 13.3123 0.754037 12.9418 0.455952C12.5714 0.157866 12.1095 -0.00319059 11.634 7.54444e-07H8.366C7.378 7.54444e-07 6.527 0.685001 6.333 1.636C6.204 2.271 5.637 2.761 4.978 2.761C3.598 2.821 2.696 2.991 1.975 3.463C1.49154 3.77996 1.07527 4.18909 0.75 4.667C0 5.767 0 7.299 0 10.364C0 13.429 5.96046e-08 14.96 0.749 16.061C1.073 16.537 1.489 16.946 1.975 17.265C3.096 18 4.657 18 7.778 18ZM10 6.273C7.699 6.273 5.833 8.104 5.833 10.363C5.833 12.622 7.7 14.456 10 14.456C12.3 14.456 14.167 12.624 14.167 10.365C14.167 8.106 12.3 6.273 10 6.273ZM10 7.909C8.62 7.909 7.5 9.008 7.5 10.364C7.5 11.719 8.62 12.818 10 12.818C11.38 12.818 12.5 11.719 12.5 10.364C12.5 9.009 11.38 7.909 10 7.909ZM14.722 7.091C14.722 6.639 15.095 6.273 15.556 6.273H16.666C17.126 6.273 17.5 6.639 17.5 7.091C17.4979 7.3099 17.409 7.519 17.2528 7.67238C17.0966 7.82576 16.8859 7.91087 16.667 7.909H15.556C15.4475 7.91006 15.3399 7.88974 15.2393 7.8492C15.1387 7.80866 15.047 7.7487 14.9696 7.67274C14.8921 7.59678 14.8304 7.50631 14.7879 7.4065C14.7454 7.30668 14.723 7.19947 14.722 7.091Z"
                    fill="white"
                  />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="text-[18px] md:text-[22px] font-semibold text-gray-800 leading-tight break-words">
                    {{ user.name }}
                  </div>
                  <div class="text-[12px] md:text-[16px] text-gray-500 font-normal leading-tight">
                    {{ user?.roles[0]?.name }}
                  </div>
                  <button
                    type="button"
                    class="change-password-trigger text-[12px] md:text-[16px] font-[400] underline mt-1 inline-flex items-center gap-2"
                    (click)="showPasswordForm()"
                  >
                    Change Password
                    <svg
                      width="20"
                      height="21"
                      viewBox="0 0 20 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.3333 7.41084C18.3333 10.3075 15.975 12.6558 13.0666 12.6558C12.5366 12.6558 11.3283 12.5342 10.7408 12.0458L10.0058 12.7775C9.5733 13.2083 9.68997 13.335 9.88247 13.5433C9.96247 13.6308 10.0558 13.7317 10.1283 13.8758C10.1283 13.8758 10.7408 14.7292 10.1283 15.5833C9.7608 16.0708 8.73163 16.7533 7.55663 15.5833L7.31163 15.8267C7.31163 15.8267 8.0458 16.6808 7.43413 17.535C7.06663 18.0225 6.08663 18.51 5.22913 17.6567L4.37246 18.51C3.78413 19.0958 3.0658 18.7542 2.7808 18.51L2.04496 17.7783C1.35913 17.095 1.75913 16.355 2.04496 16.07L8.4133 9.72834C8.4133 9.72834 7.8008 8.75334 7.8008 7.41167C7.8008 4.515 10.1591 2.16667 13.0675 2.16667C15.9758 2.16667 18.3333 4.51501 18.3333 7.41084ZM13.0675 9.24084C13.5536 9.24172 14.0201 9.04953 14.3645 8.70652C14.7089 8.36351 14.903 7.89776 14.9041 7.41167C14.9037 7.17091 14.8558 6.9326 14.7633 6.71034C14.6708 6.48808 14.5354 6.28622 14.3648 6.11628C14.1943 5.94635 13.9919 5.81168 13.7693 5.71995C13.5467 5.62822 13.3082 5.58123 13.0675 5.58167C12.8267 5.58123 12.5882 5.62822 12.3656 5.71995C12.143 5.81168 11.9407 5.94635 11.7701 6.11628C11.5996 6.28622 11.4642 6.48808 11.3716 6.71034C11.2791 6.9326 11.2312 7.17091 11.2308 7.41167C11.2319 7.89776 11.426 8.36351 11.7704 8.70652C12.1148 9.04953 12.5814 9.24172 13.0675 9.24084Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>

                <div class="flex items-center gap-2 flex-shrink-0">
                  <span
                    class="status-badge"
                    *ngIf="statusLoaded && statusLabel"
                    [ngClass]="{
                      'status-badge--inactive': statusLabel?.toLowerCase() === 'inactive',
                      'status-badge--active': statusLabel?.toLowerCase() !== 'inactive'
                    }"
                  >
                    {{ statusLabel }}
                  </span>
                  <button
                    type="button"
                    class="status-update-trigger"
                    (click)="openUserStatusModal()"
                    [attr.aria-label]="'Update status for ' + (user?.name || 'user')"
                  >
                    <svg
                      width="20"
                      height="21"
                      viewBox="0 0 20 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.83325 6.33333H4.99992C4.55789 6.33333 4.13397 6.50892 3.82141 6.82148C3.50885 7.13404 3.33325 7.55797 3.33325 7.99999V15.5C3.33325 15.942 3.50885 16.3659 3.82141 16.6785C4.13397 16.9911 4.55789 17.1667 4.99992 17.1667H12.4999C12.9419 17.1667 13.3659 16.9911 13.6784 16.6785C13.991 16.3659 14.1666 15.942 14.1666 15.5V14.6667"
                        stroke="currentColor"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M13.3333 4.66666L15.8333 7.16666M16.9875 5.9875C17.3157 5.65929 17.5001 5.21415 17.5001 4.75C17.5001 4.28585 17.3157 3.8407 16.9875 3.5125C16.6593 3.18429 16.2142 2.99991 15.75 2.99991C15.2858 2.99991 14.8407 3.18429 14.5125 3.5125L7.5 10.5V13H10L16.9875 5.9875Z"
                        stroke="currentColor"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <hr class="my-5 md:my-[30px]" />

          <!-- Personal Details -->
          <div>
            <h3 class="text-gray-700 font-semibold mb-4 text-[16px] md:text-[18px]">Personal Details</h3>
            <div class="grid grid-cols-2 gap-x-6 gap-y-4 text-gray-800">
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Full Name</p>
                <p class="text-[14px] md:text-[16px] break-words">{{ user.name }}</p>
              </div>
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Email Address</p>
                <p class="text-[14px] md:text-[16px] break-all">{{ user.email }}</p>
              </div>
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Phone</p>
                <p class="text-[14px] md:text-[16px] break-words">{{ user?.agent?.PhoneNumber }}</p>
              </div>
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Address Line 1</p>
                <p class="text-[14px] md:text-[16px] break-words">{{ user?.agent?.AddressLine1 }}</p>
              </div>
            </div>
          </div>

          <hr class="my-5 md:my-[30px]" />

          <!-- Employee Info -->
          <div>
            <h3 class="text-gray-700 font-semibold mb-4 text-[16px] md:text-[18px]">Employee Information</h3>
            <div class="grid grid-cols-2 gap-x-6 gap-y-4 space-y-4 text-gray-800">
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Employee ID</p>
                <p class="text-[14px] md:text-[16px]">{{ user.id }}</p>
              </div>
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Job Title</p>
                <p class="text-[14px] md:text-[16px]">{{ user?.roles[0]?.name }}</p>
              </div>
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Type of Employee</p>
                <p class="text-[14px] md:text-[16px]">{{ user?.roles[0]?.name }}</p>
              </div>
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Department</p>
                <p class="text-[14px] md:text-[16px]">{{ user.agent?.DepartmentID || '---' }}</p>
              </div>
              <div>
                <p class="text-gray-400 text-[12px] md:text-[14px] mb-2">Groups</p>
                <ng-container *ngIf="user.usergroup?.length > 0; else noGroups">
                  <div class="space-y-1">
                    <div *ngFor="let group of user.usergroup" class="text-[14px] md:text-[16px] text-gray-800 break-words">
                      {{ group.Name }}
                    </div>
                  </div>
                </ng-container>
                <ng-template #noGroups>
                  <p class="text-[14px] md:text-[16px] text-gray-500">No groups assigned</p>
                </ng-template>
              </div>
            </div>
          </div>

        </div>
        <!-- Password Change Form -->
        <ng-template #passwordFormTplLegacy>
          <form
            [formGroup]="passwordForm"
            (ngSubmit)="submitPasswordForm()"
            class="max-w-lg mx-auto mt-8"
          >
            <div class="mb-6">
              <label class="block text-gray-700 font-semibold mb-2"
                >Current Password
                <span
                  *ngIf="
                    passwordForm.get('currentPassword')?.valid &&
                    passwordForm.get('currentPassword')?.touched
                  "
                  class="ml-2 text-green-600 align-middle"
                  >✔️</span
                >
              </label>
              <div
                [ngClass]="{
                  'border border-green-500':
                    passwordForm.get('currentPassword')?.valid &&
                    passwordForm.get('currentPassword')?.touched,
                  'border border-gray-300':
                    !passwordForm.get('currentPassword')?.valid ||
                    !passwordForm.get('currentPassword')?.touched,
                }"
                class="rounded-xl flex items-center px-4 py-3"
              >
                <input
                  [type]="passwordVisible ? 'text' : 'password'"
                  formControlName="currentPassword"
                  class="flex-1 outline-none bg-transparent"
                />
                <button
                  type="button"
                  (click)="togglePassword('current')"
                  class="ml-2 text-gray-500 focus:outline-none"
                >
                  <span *ngIf="passwordVisible">🙈</span>
                  <span *ngIf="!passwordVisible">👁️</span>
                </button>
              </div>
            </div>
            <div class="mb-6">
              <label class="block text-gray-700 font-semibold mb-2">New Password</label>
              <div
                [ngClass]="{
                  'border border-red-400':
                    !newPasswordValid &&
                    (passwordForm.get('newPassword')?.touched || passwordTouched),
                  'border border-gray-300':
                    newPasswordValid ||
                    (!passwordForm.get('newPassword')?.touched && !passwordTouched),
                }"
                class="rounded-xl flex items-center px-4 py-3"
              >
                <input
                  [type]="newPasswordVisible ? 'text' : 'password'"
                  formControlName="newPassword"
                  class="flex-1 outline-none bg-transparent"
                />
                <button
                  type="button"
                  (click)="togglePassword('new')"
                  class="ml-2 text-gray-500 focus:outline-none"
                >
                  <span *ngIf="newPasswordVisible">🙈</span>
                  <span *ngIf="!newPasswordVisible">👁️</span>
                </button>
              </div>
              <div
                *ngIf="
                  !newPasswordValid && (passwordForm.get('newPassword')?.touched || passwordTouched)
                "
                class="text-red-500 mt-2 text-sm"
              >
                Please add all the necessary characters!
              </div>
              <ul class="mt-2 text-sm">
                <li
                  [ngClass]="{
                    'text-gray-500': passwordRequirements.minLength,
                    'text-gray-400': !passwordRequirements.minLength,
                  }"
                >
                  ● Minimum 8 characters
                </li>
                <li
                  [ngClass]="{
                    'text-gray-500': passwordRequirements.lowercase,
                    'text-gray-400': !passwordRequirements.lowercase,
                  }"
                >
                  ● One lowercase character
                </li>
                <li
                  [ngClass]="{
                    'text-red-500': !passwordRequirements.uppercase,
                    'text-gray-500': passwordRequirements.uppercase,
                  }"
                >
                  ● One uppercase character
                </li>
                <li
                  [ngClass]="{
                    'text-gray-500': passwordRequirements.special,
                    'text-gray-400': !passwordRequirements.special,
                  }"
                >
                  ● One special character
                </li>
              </ul>
            </div>
            <div class="mb-6">
              <label class="block text-gray-700 font-semibold mb-2">Confirm New Password</label>
              <div class="border border-gray-300 rounded-xl flex items-center px-4 py-3">
                <input
                  [type]="confirmPasswordVisible ? 'text' : 'password'"
                  formControlName="confirmPassword"
                  class="flex-1 outline-none bg-transparent"
                />
                <button
                  type="button"
                  (click)="togglePassword('confirm')"
                  class="ml-2 text-gray-500 focus:outline-none"
                >
                  <span *ngIf="confirmPasswordVisible">🙈</span>
                  <span *ngIf="!confirmPasswordVisible">👁️</span>
                </button>
              </div>
            </div>
            <div class="flex gap-4">
              <button
                type="submit"
                class="bg-[#472193] text-white px-6 py-2 rounded-md font-semibold"
              >
                Submit
              </button>
              <button
                type="button"
                (click)="hidePasswordForm()"
                class="bg-gray-200 text-gray-700 px-6 py-2 rounded-md font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </ng-template>
      </ng-container>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        --profile-skeleton-shell-bg: linear-gradient(180deg, #f8fafc 0%, #eef1f5 90%);
        --profile-skeleton-card-bg: #ffffff;
        --profile-skeleton-card-border: rgba(15, 23, 42, 0.08);
        --profile-skeleton-line-bg: linear-gradient(90deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.2), rgba(15, 23, 42, 0.08));
        --profile-skeleton-section-border: rgba(15, 23, 42, 0.12);
        --profile-skeleton-avatar-glow: radial-gradient(circle, rgba(15, 23, 42, 0.12) 0%, rgba(15, 23, 42, 0.02) 65%);
        --profile-skeleton-avatar-border: rgba(15, 23, 42, 0.12);
        --profile-skeleton-shell-shadow: 0 30px 60px rgba(15, 23, 42, 0.18);
        --profile-skeleton-action-pill: linear-gradient(90deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.2), rgba(15, 23, 42, 0.08));
        --profile-skeleton-action-icon: linear-gradient(90deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.2), rgba(15, 23, 42, 0.08));
      }

      :host-context(.dark),
      :host-context([data-theme='dark']) {
        --profile-skeleton-shell-bg: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0) 45%),
          radial-gradient(circle at 70% 0%, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0) 55%),
          #050608;
        --profile-skeleton-card-bg: #0e1117;
        --profile-skeleton-card-border: rgba(255, 255, 255, 0.08);
        --profile-skeleton-line-bg: #1e1e1e;
        --profile-skeleton-section-border: rgba(255, 255, 255, 0.08);
        --profile-skeleton-avatar-glow: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 65%);
        --profile-skeleton-avatar-border: rgba(255, 255, 255, 0.1);
        --profile-skeleton-shell-shadow: 0 30px 60px rgba(0, 0, 0, 0.75);
      }

      .password-change-view {
        display: flex;
        flex-direction: column;
        height: 96vh;
        min-height: 96vh;
      }

      .password-form-shell {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        height: 100%;
      }

      .password-form-content {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        margin: 0 !important;
      }

      .password-submit-footer {
        flex-shrink: 0;
        z-index: 10;
        padding: 12px 50px 16px;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
        background: #ffffff;
      }

      .password-submit-btn {
        min-width: 110px;
        height: 44px;
        padding: 0 24px;
        border-radius: 10px;
      }

      .password-submit-btn.button-color {
        background: radial-gradient(38.61% 40.02% at 50.15% 0%, #505050 0%, #121212 100%) !important;
      }

      .change-password-trigger {
        color: #472193;
      }

      .status-update-trigger {
        border: none;
        background: transparent;
        color: #0f172a;
        padding: 4px;
        border-radius: 10px;
        transition: background 0.2s ease, color 0.2s ease;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .status-update-trigger:hover {
        background: rgba(15, 23, 42, 0.08);
        color: #0b1320;
      }

      .status-update-trigger svg {
        display: block;
      }

      :host-context(.dark) .status-update-trigger {
        color: #f8fafc;
      }

      :host-context(.dark) .status-update-trigger:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      .status-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 12px 18px;
        border-radius: 52px;
        min-width: 70px;
        font-weight: 600;
        font-size: 12px;
        line-height: 1;
        text-align: center;
      }

      .status-badge--active {
        background: #e4ffed;
        color: #047857;
      }

      .status-badge--inactive {
        background: #ffe4e4;
        color: #b91c1c;
      }

      @media (max-width: 768px) {
        .password-form-content {
          padding: 12px 16px !important;
        }

        .password-submit-footer {
          padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px));
          background: #ffffff;
        }
      }

      :host-context(.dark) .password-form-content input,
      :host-context([data-theme='dark']) .password-form-content input {
        background: transparent !important;
        color: #f3f4f6 !important;
        box-shadow: none !important;
        -webkit-box-shadow: none !important;
      }

      :host-context(.dark) .password-form-content .rounded-\\[12px\\],
      :host-context([data-theme='dark']) .password-form-content .rounded-\\[12px\\] {
        background: #121212 !important;
        border-color: #2f2f2f !important;
      }

      :host-context(.dark) .password-visibility-toggle,
      :host-context([data-theme='dark']) .password-visibility-toggle {
        color: #d1d5db !important;
      }

      :host-context(.dark) .password-submit-footer,
      :host-context([data-theme='dark']) .password-submit-footer {
        background: #181818 !important;
        border-top: 1px solid #2a2a2a !important;
      }

      :host-context(.dark) .change-password-trigger,
      :host-context([data-theme='dark']) .change-password-trigger {
        color: #c4b5fd !important;
      }

      .skeleton-shell {
        background: #ffffff;
      }

      :host-context(.dark) .skeleton-shell,
      :host-context([data-theme='dark']) .skeleton-shell {
        background: #0c0c0f;
        border: 1px solid rgba(255, 255, 255, 0.06);
      }

      :host-context(.dark) .skeleton-shell ::ng-deep .skeleton-loader,
      :host-context([data-theme='dark']) .skeleton-shell ::ng-deep .skeleton-loader {
        background-color: #101014;
      }

      :host-context(.dark) .skeleton-shell ::ng-deep .skeleton-animated,
      :host-context([data-theme='dark']) .skeleton-shell ::ng-deep .skeleton-animated {
        background: linear-gradient(90deg, #101014 25%, #1c1f23 60%, #101014 75%);
      }

      :host-context(.dark),
      :host-context([data-theme='dark']) {
        --profile-skeleton-action-pill: linear-gradient(90deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.02));
        --profile-skeleton-action-icon: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
      }

      .profile-skeleton-shell {
        width: 100%;
        min-height: 100%;
        // border-radius: 32px;
        padding: clamp(24px, 4vw, 48px);
        background: var(--profile-skeleton-shell-bg);
        // box-shadow: var(--profile-skeleton-shell-shadow);
        display: flex;
        justify-content: center;
        align-items: stretch;
      }

      .profile-skeleton-card {
        width: min(960px, 100%);
        // background: var(--profile-skeleton-card-bg);
        // border-radius: 26px;
        // padding: clamp(24px, 3vw, 40px);
        // border: 1px solid var(--profile-skeleton-card-border);
        display: flex;
        flex-direction: column;
        gap: 30px;
      }

      .profile-skeleton-hero {
        display: flex;
        gap: 24px;
        align-items: center;
      }

      .profile-skeleton-avatar {
        width: 88px;
        height: 88px;
        border-radius: 50%;
        background: var(--profile-skeleton-avatar-glow);
        border: 1px solid var(--profile-skeleton-avatar-border);
        box-shadow: inset 0 0 25px rgba(0, 0, 0, 0.35);
      }

      .profile-skeleton-actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .profile-skeleton-action-pill {
        width: 80px;
        height: 34px;
        border-radius: 28px;
        background: var(--profile-skeleton-action-pill, linear-gradient(90deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.25)));
        // box-shadow: inset 0 0 14px rgba(0, 0, 0, 0.25);
      }

      .profile-skeleton-action-icon {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: var(--profile-skeleton-action-icon, linear-gradient(180deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.05)));
        // border: 1px solid rgba(255, 255, 255, 0.2);
      }

      .profile-skeleton-hero-lines,
      .profile-skeleton-row {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .profile-skeleton-section {
        border-top: 1px solid var(--profile-skeleton-section-border);
        padding-top: 18px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .profile-skeleton-section.last {
        border-bottom: 1px solid var(--profile-skeleton-section-border);
        padding-bottom: 14px;
      }

      .profile-skeleton-label,
      .profile-skeleton-line {
        height: 12px;
        border-radius: 999px;
        background: var(--profile-skeleton-line-bg);
        max-width: 100%;
      }

      .profile-skeleton-label {
        width: 34%;
        min-width: 150px;
      }

      .profile-skeleton-line.short {
        width: 38%;
      }

      .profile-skeleton-line.medium {
        width: 52%;
      }

      .profile-skeleton-line.long {
        width: 78%;
      }

      @media (max-width: 768px) {
        .profile-skeleton-shell {
          padding: 20px;
        }

        .profile-skeleton-card {
          padding: 20px;
        }
      }
    `,
  ],
})
export class UserProfileComponent implements OnInit {
  @Output() closeClicked = new EventEmitter<void>();
  user: any;
  userrole: string = '';
  statusLabel = '';
  statusLoaded = false;
  @Input() Id!: number;

  userID: any;
  showChangePassword = false;
  passwordForm!: FormGroup;
  passwordVisible = false;
  newPasswordVisible = false;
  confirmPasswordVisible = false;
  passwordValid = false;
  passwordTouched = false;

  constructor(
    private settingsService: settingsService,
    private sendDataService: SendDataService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.userID = this.sendDataService.getData();

    let id = this.userID?.__raw?.UserID || this.userID?.__raw?.id;
    this.statusLoaded = false;
    this.statusLabel = '';
    this.settingsService.getUserProfileDetails(id).subscribe({
      next: res => {
        this.user = res?.data;
        this.userrole = res?.usergroup?.Code || 'N/A';
        this.statusLabel = this.resolveStatusLabel(this.user);
        this.statusLoaded = true;
      },
      error: () => {
        this.statusLabel = '';
        this.statusLoaded = true;
      },
    });

    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    });
  }

  // Password requirements check
  get passwordRequirements() {
    const value = this.passwordForm?.get('newPassword')?.value || '';
    return {
      minLength: value.length >= 8,
      lowercase: /[a-z]/.test(value),
      uppercase: /[A-Z]/.test(value),
      special: /[^A-Za-z0-9]/.test(value),
    };
  }

  get newPasswordValid() {
    const req = this.passwordRequirements;
    return req.minLength && req.lowercase && req.uppercase && req.special;
  }

  onProfileImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const formData = new FormData();
      formData.append('userID', this.userID.id || this.userID);
      formData.append('image', file);

      this.settingsService.uploadProfileImage(formData).subscribe({
        next: res => {
          const rawUrl = res?.data?.image_url || res?.data?.imageUrl || res?.image_url || res?.imageUrl;

          if (rawUrl) {
            const imageUrl = `${rawUrl}${String(rawUrl).includes('?') ? '&' : '?'}t=${Date.now()}`;
            this.user = {
              ...this.user,
              profile_image: imageUrl,
            };
          }

          const successMessage = res?.data?.message || res?.data?.message || 'Image uploaded successfully';
          this.toastService.success('Success', successMessage);
          input.value = '';
        },
        error: err => {
          this.toastService.error('Error', err?.error?.message || 'Failed to upload image');
          input.value = '';
        },
      });
    }
  }

  onClose(): void {
    this.closeClicked.emit();
  }

  showPasswordForm() {
    this.showChangePassword = true;
  }

  hidePasswordForm() {
    this.showChangePassword = false;
    this.passwordForm.reset();
    this.passwordTouched = false;
  }

  private applyStatusUpdate(status: string): void {
    if (!this.user) {
      return;
    }
    this.user = {
      ...this.user,
      status: {
        ...(this.user.status || {}),
        StatusCode: this.formatStatusLabel(status),
      },
    };
    this.statusLabel = this.formatStatusLabel(status);
    this.statusLoaded = true;
  }

  private formatStatusLabel(status: string): string {
    if (!status) {
      return '';
    }

    return this.normalizeStatusLabel(status);
  }

  private resolveStatusLabel(data: any): string {
    if (!data) {
      return '';
    }

    const statusCandidates = [
      data?.status?.StatusCode,
      data?.status?.Status,
      data?.status?.statusCode,
      data?.status?.status,
      data?.status?.StatusName,
      data?.status?.statusName,
      data?.status?.Name,
      data?.status?.name,
      data?.StatusCode,
      data?.Status,
    ];

    for (const candidate of statusCandidates) {
      const normalized = this.normalizeStatusLabel(candidate);
      if (normalized) {
        return normalized;
      }
    }

    if (this.hasInactiveFlag(data?.IsLocked) || this.hasInactiveFlag(data?.isLocked)) {
      return 'INACTIVE';
    }

    if (this.hasActiveFlag(data?.IsLocked) || this.hasActiveFlag(data?.isLocked)) {
      return 'ACTIVE';
    }

    return '';
  }

  private normalizeStatusLabel(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    const normalized = String(value).trim();
    if (!normalized) {
      return '';
    }
    const lower = normalized.toLowerCase();
    if (['inactive', '0', 'false', 'locked'].includes(lower)) {
      return 'Inactive';
    }
    if (['active', '1', 'true', 'unlocked'].includes(lower)) {
      return 'Active';
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
  }

  private hasInactiveFlag(value: unknown): boolean {
    return ['1', 1, 'true', true, 'inactive', 'locked'].includes(String(value).trim().toLowerCase());
  }

  private hasActiveFlag(value: unknown): boolean {
    return ['0', 0, 'false', false, 'active', 'unlocked'].includes(String(value).trim().toLowerCase());
  }

  openUserStatusModal(): void {
    if (!this.user) {
      return;
    }

    this.modalService.openModal({
      id: 'user-status-modal',
      title: 'Update User Status',
      size: 'status',
      content: 'user-status-change',
      data: {
        user: this.user,
        onStatusUpdated: (newStatus: string) => this.applyStatusUpdate(newStatus),
      },
      closable: true,
      backdropClose: false,
    });
  }

  togglePassword(field: 'current' | 'new' | 'confirm') {
    if (field === 'current') this.passwordVisible = !this.passwordVisible;
    if (field === 'new') this.newPasswordVisible = !this.newPasswordVisible;
    if (field === 'confirm') this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  submitPasswordForm() {
    this.passwordTouched = true;
    const raw = this.passwordForm.value;

    if (!this.passwordForm.valid || !this.newPasswordValid) {
      return;
    }

    if (raw.newPassword !== raw.confirmPassword) {
      this.toastService.error('Error', 'New password and confirm password do not match');
      return;
    }

    const resolvedUserId =
      this.userID?.__raw?.UserID ||
      this.userID?.__raw?.id ||
      this.user?.id ||
      this.userID?.id ||
      this.userID;

    const payload = {
      CustomerId: null,
      companyId: 'UNITYCRM0021',
      agentId: String(resolvedUserId ?? ''),
      old_password: raw.currentPassword,
      new_password: raw.newPassword,
      confirm_password: raw.confirmPassword,
    };

    this.settingsService.changeUserPassword(payload).subscribe({
      next: (res: any) => {
        const ok = res?.status === true || res?.Status === true || res?.data?.Status === true;
        const message =
          (typeof res?.data === 'string' ? res.data : null) ||
          res?.message ||
          res?.data?.message ||
          (ok ? 'Password changed successfully' : 'Failed to change password');
        if (ok) {
          this.toastService.success('Success', message);
          this.hidePasswordForm();
        } else {
          this.toastService.error('Error', message);
        }
      },
      error: (err: any) => {
        this.toastService.error('Error', err?.error?.message || 'Failed to change password');
      },
    });
  }
}
